/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Input } from 'reactstrap';
import { TableData, TableRowDataInfo } from '../types';
import { MultiRowTable } from './MultiRowTable';
import { QuestionSelectingFilter } from '../../partials/question-selecting-filter/QuestionSelectingFilter';
import { Column } from 'react-table';
import { QuestionData } from '../../../utils';
import { generateId } from '../../../../../utils';
import { UtrVariable } from '../../../../summary/insights/utils/constants';
import { MAX_LENGTH_TABLE_TITLE, removeRow, updateRow } from '../utils';
import './MultiRowTableInput.scss';
import IconButton from '../../../../../components/button/IconButton';
import { useGetInitiativeBlueprintQuestionsQuery } from '../../../../../api/admin-dashboard';

const commonBtnClasses = 'w-100 rounded-0 d-flex justify-content-center align-items-center';

interface Props {
  initiativeId: string;
  tableData: TableData;
  setTableData: React.Dispatch<React.SetStateAction<TableData>>;
}

interface RowInfo {
  row: {
    id: number;
    original: TableRowDataInfo;
  };
}

type TableColumn = Column<TableRowDataInfo> & { cellProps?: { className?: string } };

interface RowForm extends UtrVariable {
  name?: string;
}

const getDefaultRowData = () => ({ code: '' });

export function MultiRowTableInput(props: Props) {
  const { initiativeId, tableData, setTableData } = props;
  const { data: blueprintQuestions = [] } = useGetInitiativeBlueprintQuestionsQuery({ initiativeId });
  const [rowData, setRowData] = React.useState<RowForm>(getDefaultRowData());
  const [isAdding, setIsAdding] = useState(true);
  const addBtnDisabled = !rowData.code;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!(rowData.code || tableData.editRowId)) {
      return;
    }
    inputRef.current?.focus();
  }, [rowData.code, tableData.editRowId]);

  const onCollapseEnterEnded = () => {
    inputRef.current?.focus();
  };

  const updateTableData = (data: Partial<TableData>) => setTableData((prev) => ({ ...prev, ...data }));
  const setRowEditId = (id: string) => updateTableData({ editRowId: id });

  const updateRowData = (data: Partial<RowForm>) => {
    setRowData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const clearData = () => {
    setRowEditId('');
    setRowData(getDefaultRowData());
    setIsAdding(false);
  };

  const onAdd = () => {
    if (!rowData.code || !blueprintQuestions) {
      return;
    }

    const { name, ...utrData } = rowData;
    const newRowData: TableRowDataInfo = {
      id: generateId(),
      data: {
        value: {
          name: name || blueprintQuestions.find((question) => question.code === rowData.code)?.name || '',
        },
        variables: [
          {
            ...utrData,
          },
        ],
      },
    };

    updateTableData({ rowData: [...tableData.rowData, newRowData], editRowId: '' });
    clearData();
  };

  const columns: TableColumn[] = [
    {
      Header: () => <>Metric titles</>,
      id: 'title',
      cellProps: {
        className: '',
      },
      Cell: (info: RowInfo) =>
        info.row.original.data.value.name ? (
          <div className='text-truncate'>{info.row.original.data.value.name}</div>
        ) : (
          <div style={{ height: '20px' }} />
        ),
    },
    {
      Header: () => <></>,
      id: 'actions',
      cellProps: {
        className: '',
      },
      Cell: (info: RowInfo) => (
        <div className='d-flex align-items-center'>
          <IconButton
            icon='fal fa-pencil-alt'
            disabled={info.row.original.id === tableData.editRowId}
            color='link'
            size='sm'
            onClick={() => handleEditRow(info)}
          />
          <IconButton
            icon='fal fa-trash-alt text-danger'
            color='link'
            size='sm'
            onClick={() => handleDeleteRow(info)}
          />
        </div>
      ),
    },
  ];

  function onEdit() {
    if (!blueprintQuestions) {
      return;
    }
    const { name, ...utrData } = rowData;
    const newRowData: Partial<TableRowDataInfo> = {
      id: tableData.editRowId,
      data: {
        value: {
          name: name || blueprintQuestions.find((question) => question.code === rowData.code)?.name || '',
        },
        variables: [
          {
            ...utrData,
          },
        ],
      },
    };
    updateTableData({
      rowData: updateRow(tableData.rowData, newRowData),
    });
    clearData();
  }

  const handleEditRow = (info: RowInfo) => {
    setIsAdding(true);
    setRowData({
      ...info.row.original.data.variables[0],
      name: info.row.original.data.value.name,
    });
    setRowEditId(info.row.original.id);
  };

  const handleDeleteRow = (info: RowInfo) => {
    updateTableData({
      rowData: removeRow(tableData.rowData, info.row.original.id),
    });
    clearData();
  };

  const handleSelectQuestion = ({ code, ...rest }: QuestionData) => {
    if (!code) {
      updateRowData({ ...rest, code: '', name: '' });
      return;
    }
    const name = blueprintQuestions.find((question) => question.code === code)?.valueLabel || '';
    updateRowData({ ...rest, code, name });
  };

  return (
    <div className=' mt-4'>
      <div className='table-input__container'>
        <MultiRowTable columns={columns} editRowId={tableData.editRowId} rowData={tableData.rowData} />
      </div>
      <Collapse isOpen={isAdding} onEntered={onCollapseEnterEnded}>
        <div key={Number(isAdding)} className='table-input__form-container'>
          <QuestionSelectingFilter
            key={tableData.editRowId}
            questionData={rowData}
            handleSelect={handleSelectQuestion}
            initiativeId={initiativeId}
          />
          <Input
            innerRef={inputRef}
            type='text'
            className='text-md mt-3'
            disabled={addBtnDisabled}
            maxLength={MAX_LENGTH_TABLE_TITLE}
            value={rowData.name ?? ''}
            onChange={(e) => updateRowData({ name: e.target.value })}
            placeholder='Metric title to appear on table (optional)'
          />
        </div>
        <>
          {tableData.editRowId === '' ? (
            <Button
              color='primary'
              className={`${commonBtnClasses} border-0 table-input__save-btn`}
              disabled={addBtnDisabled}
              onClick={onAdd}
            >
              <i className='fa fa-plus mr-2' />
              Save to table
            </Button>
          ) : (
            <Button
              color='primary'
              className={`${commonBtnClasses} border-0 table-input__save-btn`}
              disabled={addBtnDisabled}
              onClick={onEdit}
            >
              <i className='fa fa-plus mr-2' />
              Update row
            </Button>
          )}
          <Button outline onClick={clearData} className={`${commonBtnClasses} table-input__cancel-btn`}>
            <i className='fa-solid fa-angle-up mr-2'></i>
            Cancel
          </Button>
        </>
      </Collapse>
      {!isAdding && (
        <Button outline onClick={() => setIsAdding(true)} className={`${commonBtnClasses} table-input__add-btn`}>
          <i className='fas fa-plus fa-2x mr-2' />
          Add additional metric
        </Button>
      )}
    </div>
  );
}
